import { useId } from "react";
import { Button } from "../Button";
import { cx } from "../cx";
import { Icon } from "../Icon";
import styles from "./Pagination.module.css";

export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: ReadonlyArray<number>;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

/** Page numbers with ellipses: always first, last and two neighbours of the current page. */
export function pageWindow(page: number, pageCount: number): Array<number | "..."> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: Array<number | "..."> = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pageCount - 1, page + 1);
  if (from > 2) out.push("...");
  for (let p = from; p <= to; p++) out.push(p);
  if (to < pageCount - 1) out.push("...");
  out.push(pageCount);
  return out;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  className,
}: PaginationProps) {
  const sizeId = useId();
  const go = (p: number) => onPageChange(Math.min(Math.max(1, p), Math.max(1, pageCount)));
  return (
    <nav className={cx(styles.pagination, className)} aria-label="Pagination">
      {onPageSizeChange && pageSize !== undefined && (
        <label className={styles.size} htmlFor={sizeId}>
          <span>Show</span>
          <select
            id={sizeId}
            className={styles.select}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className={styles.pages}>
        <button
          type="button"
          className={styles.page}
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => go(page - 1)}
        >
          <Icon name="chevron-left" size={16} />
        </button>
        {pageWindow(page, pageCount).map((p, i) =>
          p === "..." ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: ellipses have no identity of their own
            <span key={`gap-${i}`} className={styles.gap}>
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={cx(styles.page, p === page && styles.current)}
              aria-current={p === page ? "page" : undefined}
              onClick={() => go(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.page}
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => go(page + 1)}
        >
          <Icon name="chevron-right" size={16} />
        </button>
      </div>
    </nav>
  );
}

export interface LoadMoreProps {
  onClick: () => void;
  loading?: boolean;
  /** Hides the button once everything is loaded. */
  hasMore?: boolean;
  label?: string;
}

export function LoadMore({ onClick, loading, hasMore = true, label = "Load more" }: LoadMoreProps) {
  if (!hasMore) return null;
  return (
    <div className={styles.loadMore}>
      <Button variant="secondary" loading={loading} onClick={onClick}>
        {label}
      </Button>
    </div>
  );
}
